import React, { useMemo, useEffect, useRef, useCallback } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  useUpdateNodeInternals,
  type Node,
  type Edge,
  type NodeTypes,
} from '@xyflow/react';
import { useQuery } from '@tanstack/react-query';
import { getSchemaLayout } from '../../api/schemas';
import { getDeviceContacts, getConnections, getCableToTerminalConnections } from '../../api/connections';
import { getAllProjectConnections } from '../../api/deviceInstances';
import { 
  MainSchemaDeviceNode,
  MainSchemaActuatorNode,
  TerminalSchemaDeviceNode,
  MainSchemaTerminalNode
} from '../shared/nodes';
import { EditableEdgeComponent } from '../../edges/EditableEdge';
import { Algorithm } from '../../edges/EditableEdge/constants';
import { resolveOverlappingRoutes, type RoutePoint } from '../../utils/edgeRouting';
import type { DeviceContact } from '../../types/connections';
import '@xyflow/react/dist/style.css';

export interface ViewportSchemaRendererProps {
  schemaType?: string;
  projectId: string | number;
  schemaId?: number;
  blockId?: number;
  scrollX?: number;
  scrollY?: number;
  zoom?: number;
  isInteractive?: boolean;
  isLocked?: boolean;
  onViewportChange?: (updates: { scroll_x: number; scroll_y: number; zoom: number }) => void;
  onBoundsComputed?: (bounds: { minX: number; minY: number; maxX: number; maxY: number }) => void;
}

const parseWireProperties = (rawProps: any): any => {
  if (!rawProps) return {};
  try {
    let parsed = typeof rawProps === 'string' ? JSON.parse(rawProps) : rawProps;
    if (typeof parsed === 'string') {
      parsed = JSON.parse(parsed);
    }
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (e) {
    return {};
  }
};

const mapPathPointsToControlPoints = (points: RoutePoint[]): any[] => {
  return (points || []).map((p, i) => ({ x: p.x, y: p.y, id: String(i) }));
};

const getAlgorithmFromWireProps = (type?: string): Algorithm => {
  switch (type) {
    case 'step': return Algorithm.Step;
    case 'straight': return Algorithm.Linear;
    case 'smoothstep': return Algorithm.CatmullRom;
    case 'bezier':
    default: return Algorithm.BezierCatmullRom;
  }
};

const getEdgeTypeForConnection = (fromInstanceId: number, toInstanceId: number, nodes: Node[]): string => {
  const sourceNode = nodes.find(node => node.id === fromInstanceId.toString());
  const targetNode = nodes.find(node => node.id === toInstanceId.toString());

  if (!sourceNode || !targetNode) return 'default';

  const sourceType = (sourceNode.data as any)?.deviceType || 'unknown';
  const targetType = (targetNode.data as any)?.deviceType || 'unknown';

  if (sourceType === 'actuator' && targetType === 'actuator') {
    return 'step';
  }

  return 'default';
};

const nodeTypes: NodeTypes = {
  device: MainSchemaDeviceNode,
  actuator: MainSchemaActuatorNode,
  cable: MainSchemaDeviceNode,
  terminalDevice: TerminalSchemaDeviceNode,
  autoTerminal: MainSchemaTerminalNode,
  terminalBlock: MainSchemaTerminalNode,
};

const edgeTypes = {
  'editable-edge': EditableEdgeComponent,
  custom: EditableEdgeComponent,
  default: EditableEdgeComponent,
  step: EditableEdgeComponent,
  smoothstep: EditableEdgeComponent,
  bezier: EditableEdgeComponent,
  straight: EditableEdgeComponent,
};

class SchemaErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: any }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: any) {
    return { hasError: true, error };
  }
  componentDidCatch(error: any, errorInfo: any) {
    console.error('SchemaErrorBoundary caught error in Viewport:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 bg-red-50 text-red-700 text-xs border border-red-200 m-2 rounded select-text">
          <div className="font-bold mb-1">Ошибка рендеринга видового экрана:</div>
          <div className="font-mono">{String(this.state.error?.message || this.state.error)}</div>
          <pre className="text-[10px] mt-2 overflow-auto max-h-32 text-gray-600">{this.state.error?.stack}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}

function ViewportSchemaContent({
  schemaType = 'main',
  projectId,
  schemaId,
  blockId,
  scrollX = 0,
  scrollY = 0,
  zoom = 1,
  isInteractive = false,
  isLocked = false,
  onViewportChange,
  onBoundsComputed,
}: ViewportSchemaRendererProps) {
  const { setViewport } = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();
  const lastProgrammaticVp = useRef<{ x: number; y: number; zoom: number }>({ x: -scrollX, y: -scrollY, zoom });
  const isUpdatingFromParent = useRef(false);

  // 1. Load schema layout (unwrap ApiResponse.data)
  const { data: schemaLayout = [], isLoading: isLayoutLoading } = useQuery({
    queryKey: ['schemaLayout', schemaType, schemaId, blockId],
    queryFn: async () => {
      if (schemaType === 'main' && schemaId) {
        const res = await getSchemaLayout(schemaId);
        return (res?.data || (Array.isArray(res) ? res : [])) as any[];
      } else if (schemaType === 'terminal' && blockId) {
        const res = await fetch(`/api/schemas/terminal/${blockId}/layout`);
        const d = await res.json();
        return (d.data || []) as any[];
      } else if (schemaId) {
        const res = await getSchemaLayout(schemaId);
        return (res?.data || (Array.isArray(res) ? res : [])) as any[];
      }
      return [];
    },
    enabled: !!(schemaId || blockId),
  });

  const specIdsKey = useMemo(() => {
    if (!Array.isArray(schemaLayout)) return '';
    return [...new Set(schemaLayout.map((i: any) => i.specification_id).filter(Boolean))].sort().join(',');
  }, [schemaLayout]);

  // 2. Load contacts for all devices in layout (BATCH)
  const { data: contactsBySpec = {}, isLoading: isContactsQueryLoading } = useQuery({
    queryKey: ['viewportContacts', schemaId, specIdsKey],
    queryFn: async () => {
      if (!Array.isArray(schemaLayout) || schemaLayout.length === 0) return {};
      const specIds = [...new Set(
        schemaLayout
          .filter((item: any) => item.specification_id)
          .map((item: any) => item.specification_id)
      )];
      if (specIds.length === 0) return {};
      try {
        const results = await Promise.all(
          specIds.map((specId: number) =>
            getDeviceContacts(specId, undefined, schemaId).then(contacts => ({ specId, contacts }))
          )
        );
        return results.reduce((acc, { specId, contacts }) => {
          acc[specId] = contacts;
          return acc;
        }, {} as Record<number, DeviceContact[]>);
      } catch (err) {
        console.error('Failed to batch load contacts for viewport:', err);
        return {};
      }
    },
    enabled: Array.isArray(schemaLayout) && schemaLayout.length > 0 && !!specIdsKey,
    staleTime: 60000,
  });

  // 3. Load all project connections
  const { data: projectConnections = [] } = useQuery({
    queryKey: ['projectConnections', projectId],
    queryFn: () => getAllProjectConnections(Number(projectId)),
    enabled: !!projectId && schemaType !== 'terminal',
    staleTime: 30000,
  });

  // 4. Load schema connections fallback
  const { data: schemaConnections = [] } = useQuery({
    queryKey: ['connections', schemaType, schemaId, blockId],
    queryFn: async () => {
      if (schemaType === 'terminal' && blockId) {
        const res = await fetch(`/api/connections/terminal/${blockId}`);
        const d = await res.json();
        return d.data || [];
      } else if (schemaId) {
        return getConnections(schemaId);
      }
      return [];
    },
    enabled: !!(schemaId || blockId),
  });

  // 5. Load cable to terminal connections
  const { data: cableTerminalConnections = [] } = useQuery({
    queryKey: ['cableTerminalConnections', schemaType, schemaId],
    queryFn: async () => {
      if (schemaType !== 'terminal' && schemaId) {
        return getCableToTerminalConnections(Number(schemaId));
      }
      return [];
    },
    enabled: schemaType !== 'terminal' && !!schemaId,
  });

  const noop = useCallback(() => {}, []);

  // 6. Build base device nodes
  const nodes = useMemo(() => {
    if (!Array.isArray(schemaLayout) || schemaLayout.length === 0) return [] as Node[];

    return schemaLayout
      .filter((item: any) => item.device_instance_id && item.position_x !== undefined && item.position_y !== undefined)
      .map((item: any) => {
        let nodeType = 'device';
        if (item.device_type_code === 'actuator' || item.device_type === 'actuator') {
          nodeType = 'actuator';
        } else if (item.device_type_code === 'cable' || item.device_type === 'cable' || item.device_type === 'Кабель') {
          nodeType = 'cable';
        } else if (schemaType === 'terminal') {
          nodeType = 'terminalDevice';
        }

        let parsedProperties: any = {};
        try {
          if (item.properties) {
            parsedProperties = typeof item.properties === 'string' ? JSON.parse(item.properties) : item.properties;
          }
        } catch (e) {
          console.warn('Failed to parse properties for layout item', item.id, e);
        }

        const contacts = contactsBySpec[item.specification_id] || [];

        return {
          id: item.device_instance_id.toString(),
          type: nodeType,
          position: { x: Number(item.position_x) || 100, y: Number(item.position_y) || 100 },
          draggable: false,
          selectable: false,
          data: {
            layoutId: item.id,
            deviceInstanceId: item.device_instance_id,
            specificationId: item.specification_id,
            deviceName: item.position_label || `Device #${item.device_instance_id}`,
            deviceType: item.device_type_code || item.device_type || 'device',
            deviceTypeId: item.device_type_id,
            description: item.description || '',
            specName: item.spec_name,
            powerConsumption: item.power_consumption,
            isReserve: Boolean(item.is_reserve),
            knxAddress: item.knx_address,
            contactSpacing: parsedProperties.contactSpacing,
            properties: item.properties,
            contacts: contacts,
            projectId: projectId,
            manufacturer: item.manufacturer,
            model: item.model,
            schemaType: schemaType,
            schemaId: schemaId,
            printMode: true,
            disableAutoLoadContacts: true,
            onRemoveDevice: noop,
            onEditInstance: noop,
            onConnectionsChange: noop,
          },
        } as Node;
      });
  }, [schemaLayout, contactsBySpec, schemaType, projectId, schemaId, noop]);

  // 7. Build auto terminal nodes
  const autoTerminalNodes = useMemo(() => {
    if (schemaType === 'terminal') return [] as Node[];
    if (!Array.isArray(cableTerminalConnections) || cableTerminalConnections.length === 0) return [] as Node[];

    const autoTerminals: Node[] = [];
    const cableGroups = new Map<number, any[]>();

    cableTerminalConnections
      .filter((conn: any) => conn.cable_device_type === 'cable')
      .forEach((connection: any) => {
        const cableId = connection.cable_instance_id;
        if (!cableGroups.has(cableId)) {
          cableGroups.set(cableId, []);
        }
        cableGroups.get(cableId)!.push(connection);
      });

    cableGroups.forEach((conns: any[]) => {
      if (conns.length === 0) return;
      const cableX = Number(conns[0].cable_position_x) || 100;
      const cableY = Number(conns[0].cable_position_y) || 100;

      conns.sort((a, b) => {
        const aOrder = parseInt(a.cable_contact_number?.split('-')[0]) || 0;
        const bOrder = parseInt(b.cable_contact_number?.split('-')[0]) || 0;
        return aOrder - bOrder;
      });

      const cableId = conns[0].cable_instance_id;
      const cableNode = nodes.find(n => n.id === cableId?.toString() || (n.data as any)?.deviceInstanceId === cableId);
      const cableContactSpacing = (cableNode?.data as any)?.contactSpacing as number || 18;
      const cableContactsCount = conns.length;
      const cableWidth = cableContactsCount <= 4 
        ? Math.max(90, cableContactsCount * cableContactSpacing + 20) 
        : Math.max(90, cableContactsCount * cableContactSpacing + 20);
      const terminalGroupWidth = (conns.length - 1) * 40;
      const startX = cableX + (cableWidth - terminalGroupWidth) / 2;

      conns.forEach((connection: any, index: number) => {
        const terminalX = startX + index * 40;
        const terminalY = cableY - 160;
        const terminalNodeId = `terminal-${connection.terminal_instance_id}-${connection.terminal_contact_id}`;

        autoTerminals.push({
          id: terminalNodeId,
          type: 'autoTerminal',
          position: { x: terminalX, y: terminalY },
          data: {
            terminal_instance_id: connection.terminal_instance_id,
            terminal_block_id: connection.terminal_block_id,
            terminal_block_name: connection.terminal_block_name,
            terminal_block_description: connection.terminal_block_description,
            terminal_position_in_block: connection.terminal_position_in_block,
            terminal_contact_id: connection.terminal_contact_id,
            terminal_opposite_contact_id: connection.terminal_opposite_contact_id,
            terminal_contact_label: connection.terminal_contact_label,
            terminal_opposite_contact_label: connection.terminal_opposite_contact_label,
            terminal_specification_id: connection.terminal_specification_id,
            cable_instance_id: connection.cable_instance_id,
            cable_contact_id: connection.cable_contact_id,
            connection_id: connection.connection_id,
            source_schema_id: connection.source_schema_id,
            cable_contact_number: connection.cable_contact_number,
            printMode: true,
          },
          draggable: false,
          selectable: false,
        } as Node);
      });
    });

    return autoTerminals;
  }, [cableTerminalConnections, nodes, schemaType]);

  const allNodes = useMemo(() => [...nodes, ...autoTerminalNodes], [nodes, autoTerminalNodes]);

  // Handle updates in React Flow internals when allNodes are rendered
  useEffect(() => {
    if (allNodes.length > 0) {
      const t1 = setTimeout(() => {
        allNodes.forEach(n => updateNodeInternals(n.id));
      }, 50);
      const t2 = setTimeout(() => {
        allNodes.forEach(n => updateNodeInternals(n.id));
      }, 250);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
  }, [allNodes, updateNodeInternals]);

  // Terminal lookup map
  const terminalByInstanceAndOpposite = useMemo(() => {
    const m = new Map<string, Node>();
    allNodes.forEach(n => {
      if (n.id.startsWith('terminal-')) {
        const parts = n.id.split('-');
        const data = n.data as any;
        const opp = data?.terminal_opposite_contact_id;
        if (parts.length >= 3 && opp != null) {
          m.set(`${parts[1]}-${opp}`, n);
        }
      }
    });
    return m;
  }, [allNodes]);

  // 8. Build edges with EditableEdgeComponent and resolveOverlappingRoutes
  const edges = useMemo(() => {
    const nodeMap = new Map(allNodes.map(n => [n.id, n]));

    const activeConnections = (projectConnections && projectConnections.length > 0)
      ? projectConnections
      : schemaConnections;

    if (!Array.isArray(activeConnections) || activeConnections.length === 0) {
      return [] as Edge[];
    }

    const currentSchemaId = Number(schemaId);
    const filteredConnections = activeConnections.filter((conn: any) => {
      if (!conn.from_instance_id || !conn.to_instance_id || !conn.from_contact_id || !conn.to_contact_id) {
        return false;
      }
      if (schemaType === 'terminal') return true;
      const legacy = conn.visibility_legacy === undefined ? true : !!conn.visibility_legacy;
      if (!legacy) {
        const onHome = conn.schema_id === currentSchemaId;
        const onExtra = Array.isArray(conn.visible_on_schema_ids) && conn.visible_on_schema_ids.includes(currentSchemaId);
        if (!onHome && !onExtra) return false;
      }
      return true;
    });

    const allEdges: Edge[] = [];

    // Regular edges between devices / terminals
    const regularEdges = filteredConnections.map((conn: any): Edge => {
      let sourceNodeId = conn.from_instance_id?.toString() || 'unknown-source';
      let sourceHandle = `${conn.from_contact_id || 'unknown'}-source`;

      const sourceTerminalNode = terminalByInstanceAndOpposite.get(`${conn.from_instance_id}-${conn.from_contact_id}`) ?? null;
      let sourceTerminalContactId: number | null = null;
      if (!sourceTerminalNode && cableTerminalConnections) {
        const terminalConn = cableTerminalConnections.find((c: any) => 
          c.terminal_instance_id === conn.from_instance_id &&
          c.terminal_opposite_contact_id === conn.from_contact_id
        );
        if (terminalConn) {
          sourceTerminalContactId = terminalConn.terminal_contact_id;
        }
      }

      if (sourceTerminalNode || sourceTerminalContactId) {
        if (sourceTerminalNode) {
          sourceNodeId = sourceTerminalNode.id;
        } else if (sourceTerminalContactId) {
          sourceNodeId = `terminal-${conn.from_instance_id}-${sourceTerminalContactId}`;
        }
        sourceHandle = `${conn.from_contact_id || 'unknown'}-top-source`;
      } else {
        sourceHandle = `${conn.from_contact_id || 'unknown'}-source`;
      }

      let targetNodeId = conn.to_instance_id?.toString() || 'unknown-target';
      let targetHandle = `${conn.to_contact_id || 'unknown'}-target`;

      const targetTerminalNode = terminalByInstanceAndOpposite.get(`${conn.to_instance_id}-${conn.to_contact_id}`) ?? null;
      let targetTerminalContactId: number | null = null;
      if (!targetTerminalNode && cableTerminalConnections) {
        const terminalConn = cableTerminalConnections.find((c: any) => 
          c.terminal_instance_id === conn.to_instance_id &&
          c.terminal_opposite_contact_id === conn.to_contact_id
        );
        if (terminalConn) {
          targetTerminalContactId = terminalConn.terminal_contact_id;
        }
      }

      if (targetTerminalNode || targetTerminalContactId) {
        if (targetTerminalNode) {
          targetNodeId = targetTerminalNode.id;
        } else if (targetTerminalContactId) {
          targetNodeId = `terminal-${conn.to_instance_id}-${targetTerminalContactId}`;
        }
        targetHandle = `${conn.to_contact_id || 'unknown'}-top-target`;
      } else {
        targetHandle = `${conn.to_contact_id || 'unknown'}-target`;
      }

      const wireProps: any = parseWireProperties(conn.wire_properties);
      const edgeId = `connection-${conn.id}`;

      let pathPoints: RoutePoint[] = [];
      if (conn.path_points) {
        try {
          const parsed = typeof conn.path_points === 'string' ? JSON.parse(conn.path_points) : conn.path_points;
          if (Array.isArray(parsed)) {
            pathPoints = parsed.map((p: any) => ({ x: Number(p.x) || 0, y: Number(p.y) || 0, type: 'waypoint' as const }));
          }
        } catch (error) {}
      }

      const isTerminalToTerminal = (sourceNodeId.startsWith('terminal-') && targetNodeId.startsWith('terminal-'));
      let defaultType = getEdgeTypeForConnection(conn.from_instance_id, conn.to_instance_id, allNodes);
      if (isTerminalToTerminal && !wireProps?.type) {
        defaultType = 'smoothstep';
      }

      const preservedType = wireProps?.type as string | undefined;
      const edgeType = preservedType || defaultType;
      const effectiveWireProps = { ...wireProps, type: edgeType };

      const effectiveStyle = {
        stroke: effectiveWireProps.color || effectiveWireProps.style_properties?.stroke || '#007bff',
        strokeWidth: effectiveWireProps.stroke_width || effectiveWireProps.style_properties?.strokeWidth || 2,
        strokeDasharray: effectiveWireProps.stroke_dasharray || effectiveWireProps.style_properties?.strokeDasharray,
        ...effectiveWireProps.style_properties
      };

      return {
        id: edgeId,
        source: sourceNodeId,
        sourceHandle,
        target: targetNodeId,
        targetHandle,
        type: 'editable-edge',
        animated: false,
        selected: false,
        selectable: false,
        style: effectiveStyle,
        data: {
          connectionId: conn.id,
          points: mapPathPointsToControlPoints(pathPoints),
          algorithm: getAlgorithmFromWireProps(effectiveWireProps.type),
          edgeLabels: effectiveWireProps.edge_labels || effectiveWireProps.edgeLabels || [],
          wireProperties: effectiveWireProps,
          deferLabelDragCommit: true,
          isReadOnly: true,
          isManualRoute: conn.is_manual_route === 1 || conn.is_manual_route === '1' || conn.is_manual_route === true
        }
      };
    });

    allEdges.push(...regularEdges);

    // Device-to-terminal edges (actuators to top of auto-terminal)
    if (cableTerminalConnections && cableTerminalConnections.length > 0) {
      const deviceToTerminalEdges = cableTerminalConnections
        .filter((connection: any) => {
          const nodeId = connection.cable_instance_id.toString();
          const node = nodeMap.get(nodeId);
          return node && node.data && (node.data as any).deviceType !== 'cable' && (node.data as any).deviceType !== 'Кабель';
        })
        .map((connection: any): Edge => {
          const deviceNodeId = connection.cable_instance_id.toString();
          const terminalNodeId = `terminal-${connection.terminal_instance_id}-${connection.terminal_contact_id}`;
          const sourceHandle = `${connection.cable_contact_id}-source`;
          const targetHandle = `${connection.terminal_opposite_contact_id || connection.terminal_contact_id}-top-target`;

          const edgeId = `device-terminal-${connection.connection_id}`;
          const wireProps: any = parseWireProperties(connection.wire_properties);

          let pathPoints: RoutePoint[] = [];
          if (connection.path_points) {
            try {
              const parsed = typeof connection.path_points === 'string' ? JSON.parse(connection.path_points) : connection.path_points;
              if (Array.isArray(parsed)) {
                pathPoints = parsed.map((p: any) => ({ x: Number(p.x) || 0, y: Number(p.y) || 0, type: 'waypoint' as const }));
              }
            } catch (error) {}
          }

          const edgeType = wireProps.type || 'bezier';
          const effectiveWireProps = { ...wireProps, type: edgeType };
          const effectiveStyle = {
            stroke: effectiveWireProps.color || effectiveWireProps.style_properties?.stroke || '#007bff',
            strokeWidth: effectiveWireProps.stroke_width || effectiveWireProps.style_properties?.strokeWidth || 2,
            strokeDasharray: effectiveWireProps.stroke_dasharray || effectiveWireProps.style_properties?.strokeDasharray,
            ...effectiveWireProps.style_properties
          };

          return {
            id: edgeId,
            source: deviceNodeId,
            sourceHandle,
            target: terminalNodeId,
            targetHandle,
            type: 'editable-edge',
            animated: false,
            selected: false,
            selectable: false,
            style: effectiveStyle,
            data: {
              connectionId: connection.connection_id,
              points: mapPathPointsToControlPoints(pathPoints),
              algorithm: getAlgorithmFromWireProps(effectiveWireProps.type || 'bezier'),
              edgeLabels: effectiveWireProps.edge_labels || effectiveWireProps.edgeLabels || [],
              wireProperties: effectiveWireProps,
              deferLabelDragCommit: true,
              isReadOnly: true,
              isManualRoute: connection.is_manual_route === 1 || connection.is_manual_route === '1' || connection.is_manual_route === true
            }
          };
        });

      allEdges.push(...deviceToTerminalEdges);
    }

    // Terminal-to-cable edges (bottom of auto-terminal to cable)
    if (cableTerminalConnections && cableTerminalConnections.length > 0) {
      const terminalToCableEdges = cableTerminalConnections
        .filter((connection: any) => {
          const nodeId = connection.cable_instance_id.toString();
          const node = nodeMap.get(nodeId);
          return node && node.data && ((node.data as any).deviceType === 'cable' || (node.data as any).deviceType === 'Кабель');
        })
        .map((connection: any): Edge => {
          const terminalNodeId = `terminal-${connection.terminal_instance_id}-${connection.terminal_contact_id}`;
          const cableNodeId = connection.cable_instance_id.toString();
          const sourceHandle = `${connection.terminal_contact_id}-bottom-source`;
          const targetHandle = `${connection.cable_contact_id}-target`;

          const edgeId = `auto-terminal-${connection.connection_id}`;
          const wireProps: any = parseWireProperties(connection.wire_properties);

          let pathPoints: RoutePoint[] = [];
          if (connection.path_points) {
            try {
              const parsed = typeof connection.path_points === 'string' ? JSON.parse(connection.path_points) : connection.path_points;
              if (Array.isArray(parsed)) {
                pathPoints = parsed.map((p: any) => ({ x: Number(p.x) || 0, y: Number(p.y) || 0, type: 'waypoint' as const }));
              }
            } catch (error) {}
          }

          const effectiveStyle = {
            strokeWidth: wireProps.stroke_width || wireProps.style_properties?.strokeWidth || 2,
            strokeDasharray: wireProps.stroke_dasharray || wireProps.style_properties?.strokeDasharray,
            ...wireProps.style_properties,
            stroke: '#28a745'
          };

          return {
            id: edgeId,
            source: terminalNodeId,
            sourceHandle,
            target: cableNodeId,
            targetHandle,
            type: 'editable-edge',
            animated: false,
            selected: false,
            selectable: false,
            style: effectiveStyle,
            data: {
              connectionId: connection.connection_id,
              points: mapPathPointsToControlPoints(pathPoints),
              algorithm: Algorithm.BezierCatmullRom,
              edgeLabels: wireProps.edge_labels || wireProps.edgeLabels || [],
              wireProperties: { ...wireProps, type: 'bezier' },
              deferLabelDragCommit: true,
              isReadOnly: true,
              isManualRoute: connection.is_manual_route === 1 || connection.is_manual_route === '1' || connection.is_manual_route === true
            }
          };
        });

      allEdges.push(...terminalToCableEdges);
    }

    const filteredEdges = allEdges.filter(edge =>
      nodeMap.get(edge.source) != null && nodeMap.get(edge.target) != null
    );

    // Apply automatic routing for non-manual routes (Step algorithm)
    const routes = resolveOverlappingRoutes(filteredEdges, allNodes);

    const mapped = filteredEdges.map(edge => {
      const connectionId = parseInt(edge.id.replace(/^(connection-|auto-terminal-|device-terminal-)/, ''));
      const route = routes.get(connectionId);
      const edgeData = edge.data as any;

      const isStep = edgeData?.algorithm === Algorithm.Step;

      const baseEdge = {
        ...edge,
        selectable: false,
        focusable: false,
        interactionWidth: 0,
      };

      if (route && !edgeData?.isManualRoute && isStep) {
        return {
          ...baseEdge,
          data: {
            ...edge.data,
            points: mapPathPointsToControlPoints(route.pathPoints)
          }
        };
      }

      return baseEdge;
    });

    return mapped;
  }, [
    allNodes,
    projectConnections,
    schemaConnections,
    cableTerminalConnections,
    schemaId,
    schemaType,
    terminalByInstanceAndOpposite
  ]);

  // 9. Compute bounds and notify parent
  useEffect(() => {
    if (!onBoundsComputed || allNodes.length === 0) return;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const n of allNodes) {
      const x = n.position?.x ?? 0;
      const y = n.position?.y ?? 0;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
    if (Number.isFinite(minX) && Number.isFinite(maxX)) {
      onBoundsComputed({ minX, minY, maxX, maxY });
    }
  }, [allNodes, onBoundsComputed]);

  // 10. Programmatic sync of viewport (from parent props to ReactFlow)
  useEffect(() => {
    const targetX = -Number(scrollX || 0);
    const targetY = -Number(scrollY || 0);
    const targetZoom = Number(zoom || 1);

    const diffX = Math.abs(lastProgrammaticVp.current.x - targetX);
    const diffY = Math.abs(lastProgrammaticVp.current.y - targetY);
    const diffZ = Math.abs(lastProgrammaticVp.current.zoom - targetZoom);

    if (diffX > 0.5 || diffY > 0.5 || diffZ > 0.005) {
      lastProgrammaticVp.current = { x: targetX, y: targetY, zoom: targetZoom };
      isUpdatingFromParent.current = true;
      setViewport({ x: targetX, y: targetY, zoom: targetZoom }, { duration: 0 });
      setTimeout(() => {
        isUpdatingFromParent.current = false;
      }, 50);
    }
  }, [scrollX, scrollY, zoom, setViewport]);

  // 11. Handle user move inside ReactFlow
  const handleMoveEnd = useCallback((_: any, vp: { x: number; y: number; zoom: number }) => {
    if (isUpdatingFromParent.current) return;
    if (!isInteractive || isLocked) return;

    const sx = -Number(vp.x || 0);
    const sy = -Number(vp.y || 0);
    const zm = Number(vp.zoom || 1);

    lastProgrammaticVp.current = { x: vp.x, y: vp.y, zoom: vp.zoom };

    if (onViewportChange) {
      onViewportChange({ scroll_x: sx, scroll_y: sy, zoom: zm });
    }
  }, [isInteractive, isLocked, onViewportChange]);

  if (!schemaId && !blockId) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400 text-xs select-none bg-gray-50 border border-dashed border-gray-300">
        Схема не выбрана в свойствах видового экрана
      </div>
    );
  }

  const hasSpecs = Array.isArray(schemaLayout) && schemaLayout.some((i: any) => i.specification_id);
  const isContactsReady = !hasSpecs || Object.keys(contactsBySpec).length > 0;

  if (isLayoutLoading || isContactsQueryLoading || !isContactsReady) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400 text-xs select-none bg-gray-50">
        Загрузка схемы и контактов...
      </div>
    );
  }

  return (
    <div className="w-full h-full relative overflow-hidden viewport-schema-renderer">
      <style>{`
        .viewport-schema-renderer .react-flow__edge,
        .viewport-schema-renderer .react-flow__edge-path,
        .viewport-schema-renderer .react-flow__edge-interaction,
        .viewport-schema-renderer .react-flow__edge g {
          pointer-events: none !important;
          cursor: default !important;
        }
        .viewport-schema-renderer .react-flow__edge.selected .react-flow__edge-path {
          stroke-dasharray: none !important;
          filter: none !important;
        }
      `}</style>
      <ReactFlow
        nodes={allNodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView={false}
        defaultViewport={{ x: -scrollX, y: -scrollY, zoom }}
        minZoom={0.05}
        maxZoom={10}
        zoomOnScroll={isInteractive && !isLocked}
        panOnScroll={false}
        panOnDrag={isInteractive && !isLocked}
        zoomOnDoubleClick={false}
        selectionOnDrag={false}
        nodesDraggable={false}
        nodesConnectable={false}
        nodesFocusable={false}
        edgesFocusable={false}
        edgesReconnectable={false}
        elementsSelectable={false}
        onMoveEnd={handleMoveEnd}
        className="w-full h-full select-none"
        style={{ width: '100%', height: '100%' }}
      />
    </div>
  );
}

export function ViewportSchemaRenderer(props: ViewportSchemaRendererProps) {
  return (
    <SchemaErrorBoundary>
      <ReactFlowProvider>
        <ViewportSchemaContent {...props} />
      </ReactFlowProvider>
    </SchemaErrorBoundary>
  );
}
